import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { canEnableFlow, FlowPolicyError, flowContextMatches, MRC_FLOW_LOCATION, MRC_FLOW_ORIGIN, prepareFlowEnable,
  type FlowEnableRequest, type FlowPolicyContext, type FlowPolicyReceipt, type FlowReceiver } from '../lib/mrcFlowPolicy';
import { enableMrcFlowPolicy, mrcFlowUiEnabled, readMrcFlowPolicy } from '../lib/mrcFlowPolicyTransport';

export function MrcFlowPolicyCard({ locationId }: { locationId: string }) {
  const { user, session, role, loading } = useAuth();
  if (locationId !== MRC_FLOW_LOCATION) return null;
  if (loading) return <Shell><p role="status">Checking current flow-management access…</p></Shell>;
  if (!user || !session || role !== 'superadmin') return <Shell><p>Current superadmin access is required to manage the collector flow policy.</p></Shell>;
  if (!mrcFlowUiEnabled || typeof window === 'undefined' || window.location.origin !== MRC_FLOW_ORIGIN) return <Shell><p>Flow policy management is disabled in this deployment.</p></Shell>;
  return <FlowManager key={`${locationId}:${user.id}:${role}`} locationId={locationId} userId={user.id} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return <section aria-label="MRC collector flow policy" className="rounded-lg border border-border bg-surface p-5">
    <h2 className="font-semibold text-text">Collector flow policy</h2>
    <p className="mt-1 text-xs text-text-muted">MRC · PazNetOps · Approved receiver scope only</p>
    <div className="mt-4 space-y-3 text-sm text-text-muted">{children}</div>
  </section>;
}
type Review = { context: FlowPolicyContext; request: FlowEnableRequest; reviewedAt: number };

function FlowManager({ locationId, userId }: { locationId: string; userId: string }) {
  const client = useQueryClient();
  const key = useMemo(() => ['admin-mrc-flow-policy', locationId, userId], [locationId, userId]);
  const [loaded, setLoaded] = useState(false);
  const [reason, setReason] = useState('');
  const [review, setReview] = useState<Review | null>(null);
  const [pending, setPending] = useState(false);
  const [blocked, setBlocked] = useState<unknown>(null);
  const [receipt, setReceipt] = useState<FlowPolicyReceipt | null>(null);
  const [attemptedRequestId, setAttemptedRequestId] = useState<string | null>(null);
  const inFlight = useRef(false);
  const writeAbort = useRef<AbortController | null>(null);
  const query = useQuery({ queryKey: key, enabled: loaded && !blocked && !receipt,
    queryFn: ({ signal }) => readMrcFlowPolicy(locationId, userId, signal),
    retry: false, gcTime: 0, staleTime: 0,
    refetchInterval: pending ? false : 30_000, refetchIntervalInBackground: false, refetchOnWindowFocus: 'always',
  });
  useEffect(() => () => writeAbort.current?.abort(), []);
  useEffect(() => {
    if (query.error) {
      setBlocked(query.error); setReview(null); setReason('');
      client.removeQueries({ queryKey: key, exact: true });
    }
  }, [query.error, client, key]);
  const changed = !!review && !!query.data && !flowContextMatches(review.context, query.data);
  useEffect(() => {
    if (changed) { setBlocked(new FlowPolicyError('conflict')); setReview(null); setReason(''); client.removeQueries({ queryKey: key, exact: true }); }
  }, [changed, client, key]);

  const reload = () => {
    if (inFlight.current) return;
    setReview(null); setReason(''); setReceipt(null); setBlocked(null);
    setLoaded(true);
    void client.resetQueries({ queryKey: key, exact: true });
  };
  const prepare = () => {
    if (!query.data || query.error || query.isFetching || Date.now() - query.dataUpdatedAt > 60_000) return;
    try { setReview({ context: query.data, request: prepareFlowEnable(query.data, reason, crypto.randomUUID()), reviewedAt: Date.now() }); }
    catch { setBlocked(new FlowPolicyError('invalid')); setReview(null); setReason(''); }
  };
  const submit = async () => {
    if (inFlight.current) return;
    if (!review || !query.data || query.error || query.isFetching || !flowContextMatches(review.context, query.data)
      || Date.now() - review.reviewedAt > 60_000 || Date.now() - query.dataUpdatedAt > 60_000) {
      setBlocked(new FlowPolicyError('conflict')); setReview(null); setReason(''); client.removeQueries({ queryKey: key, exact: true }); return;
    }
    inFlight.current = true; setPending(true); setAttemptedRequestId(review.request.request_id);
    const controller = new AbortController(); writeAbort.current = controller;
    try {
      await client.cancelQueries({ queryKey: key, exact: true });
      const value = await enableMrcFlowPolicy(locationId, userId, review.request, controller.signal);
      setReceipt(value); setReview(null); setReason('');
      client.removeQueries({ queryKey: key, exact: true });
    } catch (error) {
      setBlocked(error); setReview(null); setReason(''); client.removeQueries({ queryKey: key, exact: true });
    } finally { inFlight.current = false; setPending(false); writeAbort.current = null; }
  };
  const error = blocked || query.error || (changed ? new FlowPolicyError('conflict') : null);
  return <Shell>
    <button type="button" className="min-h-11 rounded-md border border-border px-3 text-primary disabled:opacity-50"
      disabled={pending || query.isFetching} onClick={reload}>Load current flow policy</button>
    <FlowPolicyView context={error ? undefined : query.data} error={error} loading={loaded && query.isPending && !error && !receipt}
      receipt={receipt} />
    {error && attemptedRequestId && <p className="break-all text-xs">Last submitted request for reconciliation: {attemptedRequestId}</p>}
    {!error && !receipt && query.data && canEnableFlow(query.data) && <>
      {!review ? <>
        <label className="block" htmlFor="mrc-flow-reason">Reason for enabling flow</label>
        <textarea id="mrc-flow-reason" value={reason} maxLength={1000} onChange={event => setReason(event.target.value)}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-text" />
        <button type="button" className="min-h-11 rounded-md border border-border px-3 text-primary disabled:opacity-50"
          disabled={query.isFetching || new TextEncoder().encode(reason.trim()).length < 10 || new TextEncoder().encode(reason.trim()).length > 1000}
          onClick={prepare}>Review flow enablement</button>
      </> : <div className="space-y-3 rounded-md border border-border p-4">
        <p className="font-medium text-text">Review: flow disabled → enabled</p>
        <p>The approved receivers shown above will replace the current receiver list. DNS remains disabled. All {review.request.patch.counter_target_ids.length} selected counter jobs remain selected.</p>
        <p>Mapping revision {review.request.mapping_version}; policy version {review.request.expected_version}. This review expires after one minute.</p>
        <p className="break-all text-xs">Current policy SHA256: {review.request.expected_payload_sha256}</p>
        <p>Reason: {review.request.reason}</p>
        <p className="break-all text-xs">Request: {review.request.request_id}</p>
        <button type="button" className="min-h-11 rounded-md bg-primary px-3 text-white disabled:opacity-50"
          disabled={pending || query.isFetching || changed} onClick={() => void submit()}>{pending ? 'Submitting reviewed request…' : 'Enable approved flow receivers'}</button>
        <button type="button" className="ml-3 min-h-11 px-3" disabled={pending} onClick={() => setReview(null)}>Cancel review</button>
      </div>}
    </>}
    <p>DNS commissioning is deferred. A saved policy does not confirm collector activation, firewall readiness, exporter packets or complete flow coverage.</p>
  </Shell>;
}

function Receivers({ label, items }: { label: string; items: FlowReceiver[] }) {
  return <div><h3 className="font-medium text-text">{label}</h3>{!items.length ? <p>None recorded.</p> : <ul className="space-y-2">{items.map(receiver =>
    <li key={receiver.id} className="break-all"><span className="font-medium">{receiver.id}</span> · UDP {receiver.listen_address}
      <p>Approved exporter addresses: {receiver.exporter_addresses.join(', ')}</p></li>)}</ul>}</div>;
}
export function FlowPolicyView({ context, error, loading, receipt }: { context?: FlowPolicyContext; error: unknown; loading: boolean; receipt?: FlowPolicyReceipt | null }) {
  if (error) {
    const kind = error instanceof FlowPolicyError ? error.kind : 'unavailable';
    const message = {
      access: 'Current superadmin access could not be verified. Policy details and prepared changes are hidden.',
      disabled: 'Flow policy management is disabled in this deployment.',
      unconfigured: 'The policy adapter or verified collector mapping is unavailable. No flow configuration is inferred.',
      conflict: 'The policy, mapping or authority changed. Load the current policy and review a new request; nothing is silently rebased.',
      invalid: 'This flow-only request cannot be prepared or accepted. Load the current policy and review its prerequisites.',
      unavailable: 'The current policy is unavailable. Previously loaded details are hidden.',
      'unknown-outcome': 'The write outcome is unknown. Do not repeat the request automatically. Load current policy and reconcile the request before another change.',
    }[kind];
    return <p role="alert">{message}</p>;
  }
  if (receipt) return <div role="status" className="space-y-1"><p>Policy configuration committed at {receipt.committed_at}.</p>
    <p>Version {receipt.version}; generation {receipt.generation}; mapping revision {receipt.mapping_version}.</p>
    <p className="break-all text-xs">Request: {receipt.request_id}</p><p className="break-all text-xs">Policy SHA256: {receipt.payload_sha256}</p>
    <p>Collector receipt and real flow observations still require verification.</p></div>;
  if (loading) return <p role="status">Loading the current policy and approved scope…</p>;
  if (!context) return <p>Load the current policy to inspect the approved scope before preparing a change.</p>;
  return <div className="space-y-3">
    <p>Flow is {context.flow_enabled ? 'enabled in policy' : 'disabled in policy'}. Policy version {context.version}; mapping revision {context.mapping_version}.</p>
    <p>Metadata storage mode: {context.metadata_mode}. {context.policy_write_ready ? 'Policy writes are available.' : 'Policy writes are disabled.'}</p>
    <p className="break-all">Reviewed management interface: {context.management_interface_guid}</p>
    <Receivers label="Current flow receivers" items={context.current_flow_receivers} />
    <Receivers label="Approved flow receivers" items={context.approved_flow_receivers} />
    <p>{context.selected_counter_target_ids.length} counter jobs are selected and will be preserved.</p>
    {!context.flow_approved && <p>Flow producer scope is not configured. A reviewed receiver and exporter mapping is required.</p>}
    {context.dns_enabled && <p>DNS is already enabled in the returned policy. This flow-only control is blocked to avoid changing it.</p>}
    {context.flow_enabled && <p>This control only enables flow. It does not disable or replace an enabled configuration.</p>}
  </div>;
}
